import type { PlannerStore } from '@/views/3d-planner/store/plannerStore';
import type { PlannerSceneViewModel } from '@/views/3d-planner/store/plannerSceneViewModel.types';
import type { PlannerSceneContext } from '@/views/3d-planner/scene/plannerSceneContext';
import { stopAll, type StopHandle } from '@/util/stopHandle';
import { watchEffect } from 'vue';
import { isPlannerMeshNode, updatePlannerMesh } from '@/views/3d-planner/scene/plannerMesh';
import { updateCatstackImage } from '@/views/3d-planner/scene/catstackImage';
import { updateCrossSectionClipping } from '@/views/3d-planner/scene/crossSection';
import { bindBoneSources } from '@/views/3d-planner/scene/bindBoneSources';
import type { LoadGeometry } from '@/lib/threeScene/nodes/meshGeometry';
import { makeLoadGeometryInOrder } from '@/lib/threeScene/nodes/meshGeometry';
import { getPlyModel } from '@/api/getPlyModel';
import { updateNode } from '@/lib/threeScene/updateNode';
import { updateImplantOutlines } from '@/views/3d-planner/scene/updateImplantOutlines';

export function updatePlannerScene(
    model: PlannerStore,
    scene: PlannerSceneViewModel,
    context: PlannerSceneContext,
): StopHandle {
    const loadGeometry = makeLoadGeometry();
    return stopAll(
        ...Object.values(scene.nodes).map((node) => {
            switch (node.type) {
                case 'mesh':
                    if (isPlannerMeshNode(node)) {
                        return updatePlannerMesh(context, scene, node, loadGeometry);
                    }
                    return updateNode(context, node);
                case 'image-plane':
                    return updateCatstackImage(context, model, scene, node);
                default:
                    return updateNode(context, node);
            }
        }),
        // Project resolved bone geometry-sources (domain data) onto the mesh nodes; re-binds
        // reactively when a new case loads, so the scene stays a function of state with no reset.
        watchEffect(() => bindBoneSources(scene.nodes, model.boneSources ?? {})),
        updateCrossSectionClipping(context, scene),
        updateImplantOutlines('axial', context, model, scene),
        updateImplantOutlines('coronal', context, model, scene),
        updateImplantOutlines('sagittal', context, model, scene),
    );
}

/**
 * We should be able to load the scene-geometry in parallel, but currently it seems
 * that when we do, the data comes back out of order.
 * */
const LOAD_GEOMETRY_IN_PARALLEL = false as const;

function makeLoadGeometry(): LoadGeometry {
    return LOAD_GEOMETRY_IN_PARALLEL ? getPlyModel : makeLoadGeometryInOrder();
}
